import {
  restaurantOrderService,
  salesInvoiceService,
  salesPaymentService,
  type RestaurantOrder,
} from '@kss/api';

/**
 * Shared helpers for the customer-facing Pay-Bill flow + the
 * panel-side Issue-Bill action.
 *
 *  - `issueBillForOrders(orderIds, customer, table)` reads the orders,
 *    sums them, creates a single `SalesInvoice`, and stamps every
 *    order with `status='Billed'` + `remarks=...|invoiceId=<id>`.
 *    Idempotent — if the orders are already Billed it returns the
 *    existing invoice id instead of creating a duplicate.
 *
 *  - `findOpenInvoiceForOrders(orderIds)` walks the orders for an
 *    invoice id encoded in remarks. Used by `useOpenBill` below
 *    until §29.5 ships a real customer-scoped endpoint.
 *
 *  - `markInvoicePaid(invoiceId, method, amount)` writes a
 *    SalesPayment row, flips the invoice status, and updates every
 *    order linked to that invoice to `status='Paid'`.
 *
 *  - `whatsappInvoiceShareUrl(...)` builds a `wa.me/<phone>?text=...`
 *    deep-link with a pre-baked message.
 */

interface ParsedOrder {
  id: number;
  status?: string;
  remarks?: string;
  customerName?: string;
  customerPhone?: string;
  tableCode?: string;
  items: Array<{ name: string; quantity: number; unitPrice: number; taxRate?: number }>;
  subtotal: number;
}

function parseOrder(r: RestaurantOrder): ParsedOrder {
  let payload: {
    customerName?: string; customerPhone?: string; tableCode?: string;
    items?: Array<{ name: string; quantity: number; unitPrice: number; taxRate?: number }>;
    subtotal?: number;
  } = {};
  try {
    if (r.description && r.description.startsWith('{')) payload = JSON.parse(r.description);
  } catch { /* */ }
  const items = payload.items ?? [];
  const subtotal = payload.subtotal ?? items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  return {
    id: Number(r.id),
    status: r.status ?? undefined,
    remarks: r.remarks ?? undefined,
    customerName: payload.customerName,
    customerPhone: payload.customerPhone,
    tableCode: payload.tableCode,
    items,
    subtotal,
  };
}

export interface IssuedBill {
  invoiceId: string;
  invoiceNumber?: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  alreadyExisted: boolean;
}

/**
 * Locates an invoice id already encoded into the orders' remarks
 * audit trail. Returns null when no invoice has been issued yet.
 */
export function findInvoiceIdInRemarks(remarks: string | undefined): string | null {
  if (!remarks) return null;
  const m = /invoiceId=([0-9a-f-]+|\d+)/i.exec(remarks);
  return m ? m[1] : null;
}

export async function findOpenInvoiceForOrders(orderIds: string[]): Promise<string | null> {
  for (const id of orderIds) {
    try {
      const row = await restaurantOrderService.get(id);
      const found = findInvoiceIdInRemarks(row.remarks ?? undefined);
      if (found) return found;
    } catch { /* */ }
  }
  return null;
}

/**
 * Bundles the customer's open orders into a single SalesInvoice.
 * Idempotent: if any order already has an invoiceId in remarks,
 * that invoice is returned untouched.
 */
export async function issueBillForOrders(
  orderIds: string[],
  table?: { tableCode?: string; tableId?: string } | null,
): Promise<IssuedBill> {
  if (orderIds.length === 0) throw new Error('No orders to bill');

  // 1. Fetch every order and check for an existing invoice
  const orders: ParsedOrder[] = [];
  for (const id of orderIds) {
    try {
      const row = await restaurantOrderService.get(id);
      const existingInvoice = findInvoiceIdInRemarks(row.remarks ?? undefined);
      if (existingInvoice) {
        return {
          invoiceId: existingInvoice,
          subtotal: 0,
          taxAmount: 0,
          total: 0,
          alreadyExisted: true,
        };
      }
      orders.push(parseOrder(row));
    } catch { /* skip missing */ }
  }
  if (orders.length === 0) throw new Error('Could not load any orders to bill');

  // 2. Aggregate lines
  const allItems = orders.flatMap((o) => o.items);
  const subtotal = allItems.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const taxAmount = allItems.reduce(
    (s, i) => s + (i.unitPrice * i.quantity) * ((i.taxRate ?? 0) / 100),
    0,
  );
  const total = subtotal + taxAmount;
  const customerName = orders.find((o) => o.customerName)?.customerName ?? '';
  const tableCode = table?.tableCode ?? orders.find((o) => o.tableCode)?.tableCode ?? '';

  // 3. Create the SalesInvoice. We use the lowest-common-denominator
  // shape the live CRUD endpoint accepts (the same JSON-blob in
  // description trick we already use for orders) so the bill works
  // today against the raw `SalesInvoice` table. §31 covers the
  // proper schema.
  // §35 live SalesInvoice schema: the grid + KPIs read grandTotal +
  // balance + invoiceStatus, and `status` is the numeric row-state
  // (0=Active). Writing the legacy `{status:'Pending'}` shape made the
  // dine bill save with grandTotal=0 (invisible/₹0 in the list). Write
  // the real columns. The `DIN-` code prefix tags it as the Dine-in
  // source in the invoice grid's Source column (see G65).
  const invoiceCode = `DIN-${Date.now().toString(36).toUpperCase().slice(-8)}`;
  const grand = Math.round(total);
  const payload = {
    code: invoiceCode,
    name: `${tableCode || 'Table'} — ${customerName || 'Guest'}`,
    invoiceNumber: invoiceCode,
    invoiceDate: new Date().toISOString().slice(0, 10),
    customerName: customerName || 'Guest',
    currency: 'INR',
    invoiceStatus: 'Posted',
    paymentStatus: 'Unpaid',
    subTotal: round2(subtotal),
    discountAmount: 0,
    taxAmount: round2(taxAmount),
    roundingAmount: round2(grand - total),
    grandTotal: grand,
    paidAmount: 0,
    balance: grand,
    totalAmount: grand,
    // keep the itemised blob so the customer Pay-Bill view can re-render
    // the line list (it parses description), AND so we don't lose the lines
    // even though the SalesInvoiceLine table isn't populated for dine bills.
    description: JSON.stringify({
      tableCode, customerName, lines: allItems,
      subtotal, taxAmount, total, orderIds,
      issuedAt: new Date().toISOString(),
    }).slice(0, 1900),
    status: 0,
    remarks: `dine;subtotal=${subtotal};total=${total}`,
  };
  const created = await salesInvoiceService.create([payload as unknown as Parameters<typeof salesInvoiceService.create>[0][number]]);
  const row = (created?.[0] ?? null) as { id?: number | string; salesInvoiceId?: string } | null;
  const invoiceId = String(row?.salesInvoiceId ?? row?.id ?? '');
  if (!invoiceId) throw new Error('Invoice create returned no id');

  // 4. Stamp each order with status=Billed + invoiceId in remarks
  await Promise.all(
    orders.map((o) =>
      restaurantOrderService
        .update([
          {
            id: o.id,
            // workflow stage → string `orderStatus`; numeric `status` stays 0
            status: 0,
            orderStatus: 'Billed',
            remarks: appendAudit(o.remarks, 'Billed', invoiceId),
            isActive: 1,
          } as unknown as Parameters<typeof restaurantOrderService.update>[0][number],
        ])
        .catch(() => undefined),
    ),
  );

  return { invoiceId, invoiceNumber: invoiceCode, subtotal, taxAmount, total, alreadyExisted: false };
}

/**
 * Records a payment + flips invoice/orders to Paid.
 */
export async function markInvoicePaid(
  invoiceId: string,
  amount: number,
  method: 'UPI' | 'Cash' | 'Card',
  orderIds: string[],
): Promise<void> {
  // 1. SalesPayment row
  const payload = {
    code: `PAY-${Date.now()}`,
    name: `${method} payment for ${invoiceId}`,
    description: JSON.stringify({ invoiceId, amount, method, paidAt: new Date().toISOString() }).slice(0, 1900),
    // numeric row enum (0=Active) — a string here 400s with invalid_body.
    status: 0,
    paymentStatus: 'Settled',
    remarks: `method=${method};amount=${amount};invoice=${invoiceId}`,
    isActive: 1,
  };
  await salesPaymentService
    .create([payload as unknown as Parameters<typeof salesPaymentService.create>[0][number]])
    .catch(() => undefined);

  // 2. Flip the invoice
  try {
    const row = await salesInvoiceService.get(invoiceId);
    await salesInvoiceService.update([
      {
        ...(row as object),
        id: Number(invoiceId),
        status: 0,
        paymentStatus: 'Paid',
        invoiceStatus: 'Paid',
        remarks: `${row.remarks ?? ''}|paidAt=${new Date().toISOString().slice(11, 16)}|method=${method}`,
        isActive: 1,
      } as unknown as Parameters<typeof salesInvoiceService.update>[0][number],
    ]);
  } catch { /* invoice may not be CRUD-fetchable; skip */ }

  // 3. Flip the orders
  await Promise.all(
    orderIds.map((id) =>
      restaurantOrderService.get(id).then((row) =>
        restaurantOrderService.update([
          {
            id: Number(id),
            status: 0,
            orderStatus: 'Paid',
            remarks: appendAudit(row.remarks ?? undefined, 'Paid'),
            isActive: 1,
          } as unknown as Parameters<typeof restaurantOrderService.update>[0][number],
        ]).catch(() => undefined),
      ).catch(() => undefined),
    ),
  );
}

/**
 * Build a `wa.me/<phone>?text=...` deep-link to share the invoice
 * with the customer over WhatsApp. The link auto-opens WhatsApp on
 * mobile and WhatsApp Web on desktop.
 */
export function whatsappInvoiceShareUrl(
  phone: string,
  bill: { invoiceNumber?: string; total: number; tableCode?: string },
  inviteUrl?: string,
): string {
  const cleaned = (phone || '').replace(/[^\d]/g, '').replace(/^0+/, '');
  // wa.me requires the country code; if the user typed an Indian 10-digit
  // number bare we default to +91.
  const intl = cleaned.length === 10 ? '91' + cleaned : cleaned;

  const lines: string[] = [];
  lines.push('Hi! Thanks for dining with us today. 🙏');
  if (bill.tableCode) lines.push(`Table: *${bill.tableCode}*`);
  if (bill.invoiceNumber) lines.push(`Invoice: *${bill.invoiceNumber}*`);
  lines.push(`Total: *₹${bill.total.toFixed(2)}*`);
  if (inviteUrl) lines.push(`Receipt: ${inviteUrl}`);
  lines.push('');
  lines.push('We hope to see you again soon! ✨');
  const text = encodeURIComponent(lines.join('\n'));
  return `https://wa.me/${intl}?text=${text}`;
}

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function appendAudit(prev: string | undefined, newStatus: string, invoiceId?: string): string {
  const stamp = new Date().toISOString().slice(11, 16);
  const parts: string[] = prev ? prev.split('|').slice(-3) : [];
  parts.push(`${newStatus}@${stamp}`);
  if (invoiceId && !parts.some((p) => p.startsWith('invoiceId='))) {
    parts.push(`invoiceId=${invoiceId}`);
  }
  return parts.join('|');
}
